import React, { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import Web3 from 'web3';

import ContractContext from '../../ContractContext';
import { useWeb3React } from "@web3-react/core";

const API_KEY = process.env.REACT_APP_API_KEY;
const API_SECRET = process.env.REACT_APP_API_SECRET;

const ImageUploader = () => {
    const { contracts } = useContext(ContractContext);
    const { account, library } = useWeb3React();
    const [tokenAddress, setTokenAddress] = useState("");
    const [poolId, setPoolId] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [isExisting, setIsExisting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const toast = useRef(null);

    const WBNB_ADDRESS = "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";

    const isWalletConnected = () => account !== null;

    const getReflectionPoolAtERC20 = useCallback(async (address) => {
        if (!isWalletConnected()) {
            toast.current.show({ severity: 'error', summary: 'Wallet Error', detail: 'Please connect to a wallet.', life: 3000 });
            return;
        }
        if (!contracts.rp_rewardPacks) {
            toast.current.show({ severity: 'error', summary: 'Contract Error', detail: 'Smart Rewards contract not available.', life: 3000 });
            return;
        }

        try {
            const result = await contracts.rp_rewardPacks.methods.reflectionPoolAtERC20(address).call();
            const poolID = result.index;
            const validPoolId = poolID !== '0' || address === WBNB_ADDRESS;
            setIsExisting(validPoolId);
            setPoolId(validPoolId ? poolID.toString() : "");
            if (!validPoolId) {
                toast.current.show({ severity: 'warn', summary: 'Invalid Pool', detail: 'No valid pool ID found.', life: 3000 });
            } else {
                toast.current.show({ severity: 'success', summary: 'Valid Pool', detail: 'A valid pool ID was found.', life: 3000 });
            }
            return validPoolId ? poolID.toString() : null;
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Fetching Error', detail: 'Failed to fetch pool ID.', life: 3000 });
            setIsExisting(false);
            setPoolId("");
        }
    }, [contracts.rp_rewardPacks, account]);

    useEffect(() => {
        if (tokenAddress) {
            getReflectionPoolAtERC20(tokenAddress);
        }
    }, [tokenAddress, getReflectionPoolAtERC20]);

    const handleTokenAddressChange = (e) => {
        setTokenAddress(e.target.value);
        // Reset poolId and image selection when address changes
        setPoolId("");
        setSelectedImage(null);
    };

    const handleImageChange = (event) => {
        if (event.files && event.files[0]) {
            setSelectedImage(event.files[0]);
            const reader = new FileReader();
            reader.onloadend = () => {
               
            };
            reader.readAsDataURL(event.files[0]);
        }
    };

    const isFilenameValid = () => {
        if (!selectedImage || !tokenAddress) return false;
        const web3 = new Web3(library.provider);
        const checksumAddress = web3.utils.toChecksumAddress(tokenAddress);
        const expectedFilename = `${checksumAddress}.png`;
        return selectedImage.name === expectedFilename;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedImage) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Please select an image.', life: 3000 });
            return;
        }
        setUploading(true);
        try {
            const web3 = new Web3(library.provider);
            const checksumAddress = web3.utils.toChecksumAddress(tokenAddress);

            const result = await uploadMedia({ address: checksumAddress, poolId }, selectedImage, setUploadProgress);
            if (result) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Image uploaded successfully.', life: 3000 });
                // Clear form only on successful upload
                setTokenAddress("");
                setPoolId("");
                setSelectedImage(null);
            } else {
                throw new Error('Failed to upload');
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Upload failed: ' + error.message, life: 3000 });
        }
        setUploading(false);
        setUploadProgress(0);
    };

    return (
        <div style={{ maxWidth: "500px", margin: "auto", padding: "20px" }}>
            <Toast ref={toast} />
            <Card title="Reward Pack Image Uploader" style={{ padding: "20px" }}>
                <form onSubmit={handleSubmit} style={{ marginTop: '15px' }}>
                    <div className="p-field" style={{ marginBottom: "20px" }}>
                        <label htmlFor="address">Token Address</label>
                        <InputText id="address" value={tokenAddress} onChange={handleTokenAddressChange} />
                    </div>
                    <div className="p-field" style={{ marginBottom: "20px" }}>
                        <label htmlFor="image">Upload Image</label>
                        <FileUpload
                            id="image"
                            name="image"
                            accept="image/*"
                            maxFileSize={1000000}
                            customUpload
                            auto
                            onSelect={handleImageChange}
                            style={{ marginTop: '10px' }}
                        />
                    </div>
                    <div className="p-field" style={{ marginBottom: "20px" }}>
                        <Button label="Upload Image" type="submit" disabled={!isExisting || !selectedImage || !isFilenameValid() || uploading} />
                    </div>
                    {uploading && (
                        <div className="p-field" style={{ marginBottom: "20px" }}>
                            <ProgressBar value={uploadProgress} />
                            <div style={{ textAlign: 'center', marginTop: '10px' }}>{uploadProgress}%</div>
                        </div>
                    )}
                </form>
            </Card>
        </div>
    );
};

export default ImageUploader;

const createAuthHeaders = () => {
    return {
        'Content-Type': 'application/json',
        'x-floatie-api-key': API_KEY,
        'x-floatie-api-secret': API_SECRET,
    };
};

async function uploadMedia(token, imageData, setUploadProgress) {
    if (!imageData) {
        console.log('No image data provided');
        return;
    }

    console.log('Preparing to upload media for token:', token);

    const signatureData = {
        name: token.address, // Using token's address as name
        mimeType: imageData.type, // mimeType from the image data
        mediaType: 'icon', // As specified
    };

    try {
        console.log('Requesting upload signature...');
        const signatureResponse = await fetch('https://api.rocketfi.app/v1/media', {
            method: 'POST',
            headers: createAuthHeaders(),
            body: JSON.stringify(signatureData),
        });

        if (!signatureResponse.ok) {
            console.error('Failed to obtain upload signature:', signatureResponse.statusText);
            return;
        }

        const signatureResponseData = await signatureResponse.json();
        console.log('Upload signature response:', signatureResponseData);

        if (signatureResponseData.status === 200 && signatureResponseData.result) {
            console.log('Uploading image to signed URL...');
            const putResponse = await fetch(signatureResponseData.result.signedUrl, {
                method: 'PUT',
                headers: {
                    'content-type': imageData.type,
                },
                body: imageData,
                onUploadProgress: (progressEvent) => {
                    const totalLength = progressEvent.lengthComputable ? progressEvent.total : imageData.size;
                    if (totalLength) {
                        setUploadProgress(Math.round((progressEvent.loaded * 100) / totalLength));
                    }
                }
            });

            if (putResponse.ok) {
                console.log('Image uploaded successfully:', signatureResponseData.result);
                token.mediaId = signatureResponseData.result.media.mediaId;
                token.customizedAt = new Date();
                await updateToken(token);
                return signatureResponseData.result; // Return the result for further processing
            } else {
                console.error('Failed to upload image to signed URL');
            }
        } else {
            console.error('Invalid response for upload signature:', signatureResponseData);
        }
    } catch (error) {
        console.error('Error in uploadMedia:', error);
    }

    return null;
}

async function updateToken(token) {
    console.log('Updating token:', token);

    try {
        const response = await fetch(`https://api.rocketfi.app/v1/tokens/${token.address}`, {
            method: 'PUT',
            headers: createAuthHeaders(),
            body: JSON.stringify(token),
        });

        const data = await response.json();
        console.log('Response from token update:', data);

        if (data.status !== 200) {
            console.error('Token update failed with status:', data.status);
        } else {
            console.log('Token updated successfully');
        }
    } catch (error) {
        console.error('Error in updateToken:', error);
    }
}
